/** Units: showing and entering depths, temperatures and pressures in the
 * diver's choice of metric (m, °C, bar) or imperial (ft, °F, psi).
 *
 * The API stores and sends every measurement metric; only what a person
 * sees or types is converted, here. The app's lib/utils/units.dart does
 * the same with the same factors and rounding: change both together.
 */

export const METRIC = "metric";
export const IMPERIAL = "imperial";

export const FEET_PER_METRE = 3.28084;
export const PSI_PER_BAR = 14.5038;

const LABELS = {
  [METRIC]: { depth: "m", temperature: "°C", pressure: "bar" },
  [IMPERIAL]: { depth: "ft", temperature: "°F", pressure: "psi" },
};

// Between a number and its unit, so "32 m" never breaks across lines.
const NBSP = " ";

/** A user's units: imperial only if they chose it. Signed out, a missing
 * field (an older API) or anything unknown is metric, the default. */
export function unitsOf(user) {
  return user?.units === IMPERIAL ? IMPERIAL : METRIC;
}

/** The other system: what a per-dive m / ft switch flips to. */
export function otherUnits(units) {
  return units === IMPERIAL ? METRIC : IMPERIAL;
}

/** The unit's symbol: unitLabel("depth", "imperial") is "ft". */
export function unitLabel(quantity, units) {
  return LABELS[units === IMPERIAL ? IMPERIAL : METRIC][quantity];
}

// ---- metric (as stored) to the diver's units, and back ----

export function depthIn(metres, units) {
  return units === IMPERIAL ? metres * FEET_PER_METRE : metres;
}

export function depthToMetres(value, units) {
  return units === IMPERIAL ? value / FEET_PER_METRE : value;
}

export function temperatureIn(celsius, units) {
  return units === IMPERIAL ? (celsius * 9) / 5 + 32 : celsius;
}

export function temperatureToCelsius(value, units) {
  return units === IMPERIAL ? ((value - 32) * 5) / 9 : value;
}

export function pressureIn(bar, units) {
  return units === IMPERIAL ? bar * PSI_PER_BAR : bar;
}

export function pressureToBar(value, units) {
  return units === IMPERIAL ? value / PSI_PER_BAR : value;
}

// ---- for showing ----

/** value rounded to `decimals` places, without a trailing ".0" or a "-0". */
function rounded(value, decimals) {
  const text = value.toFixed(decimals).replace(/\.0+$/, "");
  return text === "-0" ? "0" : text;
}

function format(value, decimals, label) {
  if (value == null || !Number.isFinite(value)) return "";
  return `${rounded(value, decimals)}${NBSP}${label}`;
}

/** "9.3 m" or "31 ft". Metres to a tenth, as dive computers show them;
 * feet whole. Empty for a missing value. */
export function formatDepth(metres, units) {
  if (metres == null) return "";
  return format(depthIn(metres, units), units === IMPERIAL ? 0 : 1, unitLabel("depth", units));
}

/** "10 °C" or "50 °F", in whole degrees. */
export function formatTemperature(celsius, units) {
  if (celsius == null) return "";
  return format(temperatureIn(celsius, units), 0, unitLabel("temperature", units));
}

/** "180 bar" or "2611 psi", whole. */
export function formatPressure(bar, units) {
  if (bar == null) return "";
  return format(pressureIn(bar, units), 0, unitLabel("pressure", units));
}
